"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumHarnessLocalNamingRule = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const AltiumLabelTypes_1 = require("./AltiumLabelTypes");
const AltiumNetLocalNamingRule_1 = require("./AltiumNetLocalNamingRule");
const AltiumLabelTypeNameUtil_1 = require("./AltiumLabelTypeNameUtil");
const AltiumBusRange_1 = require("../helpers/AltiumBusRange");
var HarnessHierarchyIdentityKind;
(function (HarnessHierarchyIdentityKind) {
    HarnessHierarchyIdentityKind["Port"] = "port";
    HarnessHierarchyIdentityKind["SheetEntry"] = "sheet-entry";
})(HarnessHierarchyIdentityKind || (HarnessHierarchyIdentityKind = {}));
class AltiumHarnessLocalNamingRule extends AltiumNetLocalNamingRule_1.AltiumNetLocalNamingRule {
    constructor() {
        super(...arguments);
        this.sheetEntryCarrierIndexes = new WeakMap();
    }
    apply(hierarchy) {
        this.sheetEntryCarrierIndexes = new WeakMap();
        const harnessHierarchy = hierarchy;
        this.expandRecursive(harnessHierarchy);
        this.cleanupRecursive(harnessHierarchy);
    }
    expandRecursive(hierarchy) {
        this.expandHarnessIslands(hierarchy);
        for (const child of hierarchy.children) {
            this.expandRecursive(child);
        }
    }
    cleanupRecursive(hierarchy) {
        this.removeCarrierObjects(hierarchy.combobulator);
        for (const child of hierarchy.children) {
            this.cleanupRecursive(child);
        }
    }
    expandHarnessIslands(hierarchy) {
        if (!hierarchy.combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
            return;
        }
        const harnessDimension = hierarchy.combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
        const processed = new Set();
        const islands = [];
        for (const object of Array.from(harnessDimension.iterateAllObjects())) {
            if (processed.has(object) || !this.isCarrierObject(object)) {
                continue;
            }
            const startPosition = this.getObjectPositionInDimension(object, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
            if (!startPosition) {
                continue;
            }
            const island = Array.from(harnessDimension.getObjectsConnectedToPosition(startPosition)).filter((obj) => this.isCarrierObject(obj));
            for (const islandObject of island) {
                processed.add(islandObject);
            }
            islands.push(island);
        }
        const islandIndexByLabelFold = new Map();
        const mergeParents = islands.map((_, index) => index);
        const findRoot = (index) => {
            let root = index;
            while (mergeParents[root] !== root) {
                root = mergeParents[root];
            }
            while (mergeParents[index] !== root) {
                const next = mergeParents[index];
                mergeParents[index] = root;
                index = next;
            }
            return root;
        };
        for (let i = 0; i < islands.length; i++) {
            for (const name of this.collectCarrierBaseNamesForSources(islands[i], ['carrier-net-label'])) {
                const fold = name.toLowerCase();
                const existing = islandIndexByLabelFold.get(fold);
                if (existing === undefined) {
                    islandIndexByLabelFold.set(fold, i);
                    continue;
                }
                mergeParents[findRoot(i)] = findRoot(existing);
            }
        }
        const islandIdentities = islands.map((island) => this.collectTypedHierarchyIdentities(hierarchy, island));
        const islandIsPureLocalBreakout = islands.map((island, index) => this.islandHasLocalBreakout(island) &&
            !islandIdentities[index].some((identity) => identity.kind === HarnessHierarchyIdentityKind.SheetEntry));
        const islandIndexesByRoot = islands.map(() => []);
        for (let i = 0; i < islands.length; i++) {
            islandIndexesByRoot[findRoot(i)].push(i);
        }
        for (let i = 0; i < islands.length; i++) {
            for (let j = i + 1; j < islands.length; j++) {
                const rootI = findRoot(i);
                const rootJ = findRoot(j);
                if (rootI === rootJ) {
                    continue;
                }
                const bothPureLocal = islandIsPureLocalBreakout[i] && islandIsPureLocalBreakout[j];
                if (!this.identitiesShareCompatibleMatch(islandIdentities[i], islandIdentities[j], bothPureLocal) ||
                    this.pureLocalBreakoutIsRelayAmbiguous(islandIdentities[i], islandIsPureLocalBreakout[i], islandIdentities[j], islandIsPureLocalBreakout[j])) {
                    continue;
                }
                if (this.mergeViolatesIslandGuards(islandIdentities, islandIsPureLocalBreakout, islandIndexesByRoot[rootI], islandIndexesByRoot[rootJ])) {
                    continue;
                }
                mergeParents[rootJ] = rootI;
                islandIndexesByRoot[rootI] = islandIndexesByRoot[rootI].concat(islandIndexesByRoot[rootJ]);
                islandIndexesByRoot[rootJ] = [];
            }
        }
        const mergedIslands = new Map();
        for (let i = 0; i < islands.length; i++) {
            const root = findRoot(i);
            mergedIslands.set(root, (mergedIslands.get(root) ?? []).concat(islands[i]));
        }
        islands.length = 0;
        islands.push(...mergedIslands.values());
        islands.sort((a, b) => {
            const aHasBase = !!this.collectCarrierBaseName(hierarchy.combobulator, a);
            const bHasBase = !!this.collectCarrierBaseName(hierarchy.combobulator, b);
            if (aHasBase === bHasBase) {
                return 0;
            }
            return aHasBase ? -1 : 1;
        });
        const compatibleMembersByBaseName = this.collectCompatibleMembersByBaseName(hierarchy, islands);
        for (const island of islands) {
            this.expandHarnessIsland(hierarchy, island, compatibleMembersByBaseName);
        }
    }
    collectCompatibleMembersByBaseName(hierarchy, islands) {
        const compatibleMembersByBaseName = new Map();
        const carrierPositionKey = this.createDimensionPositionKeyer(hierarchy.combobulator, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
        for (const island of islands) {
            const baseName = this.collectCarrierBaseName(hierarchy.combobulator, island);
            if (!baseName) {
                continue;
            }
            const targets = this.collectTargets(hierarchy, island);
            if (targets.length === 0) {
                continue;
            }
            const targetMembers = this.resolveTargetMembers(island, targets, carrierPositionKey);
            let compatibleMembers = compatibleMembersByBaseName.get(baseName.name);
            if (!compatibleMembers) {
                compatibleMembers = new Set();
                compatibleMembersByBaseName.set(baseName.name, compatibleMembers);
            }
            for (const members of targetMembers.values()) {
                for (const member of members) {
                    compatibleMembers.add(member);
                }
            }
        }
        return new Map(Array.from(compatibleMembersByBaseName.entries()).map(([baseName, members]) => [
            baseName,
            Array.from(members).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b)),
        ]));
    }
    expandHarnessIsland(hierarchy, island, compatibleMembersByBaseName) {
        const targets = this.collectTargets(hierarchy, island);
        if (targets.length === 0) {
            return;
        }
        const carrierPositionKey = this.createDimensionPositionKeyer(hierarchy.combobulator, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
        const baseName = this.collectCarrierBaseName(hierarchy.combobulator, island);
        const targetMembers = this.resolveTargetMembers(island, targets, carrierPositionKey, baseName ? compatibleMembersByBaseName.get(baseName.name) : undefined);
        const childMemberNames = this.collectAliasedChildMemberNames(hierarchy, targets);
        const islandAnchor = island
            .map((object) => object.endpoints[0]?.position)
            .find((position) => position !== undefined) ?? [0, 0];
        const islandKey = `${islandAnchor[0]}:${islandAnchor[1]}`;
        const memberNames = new Map();
        if (baseName) {
            for (const members of targetMembers.values()) {
                for (const member of members) {
                    memberNames.set(member, childMemberNames.get(member) ?? `${baseName.name}.${member}`);
                }
            }
        }
        else {
            for (const target of targets) {
                if (target.source !== 'connector-entry') {
                    continue;
                }
                const members = targetMembers.get(target) ?? [];
                const existingName = this.findRegularNetLabelAtPosition(hierarchy.combobulator, target.position, target.dimension);
                if (!existingName) {
                    continue;
                }
                for (const member of members) {
                    memberNames.set(member, existingName);
                }
            }
        }
        for (const target of targets) {
            if (target.source !== 'connector-entry') {
                continue;
            }
            const explicitName = this.findRegularNetLabelAtPosition(hierarchy.combobulator, target.position, target.dimension);
            if (!explicitName) {
                continue;
            }
            for (const member of targetMembers.get(target) ?? []) {
                if (baseName?.source === 'carrier-net-label' && explicitName.toLowerCase() === member.toLowerCase()) {
                    continue;
                }
                if (this.isCompatibleChildMemberName(explicitName, member)) {
                    memberNames.set(member, explicitName);
                }
            }
        }
        if (memberNames.size === 0) {
            return;
        }
        const regularConnectorTargetsByMember = new Map();
        const connectorCountByMember = new Map();
        for (const target of targets) {
            if (target.source !== 'connector-entry') {
                continue;
            }
            for (const member of targetMembers.get(target) ?? []) {
                connectorCountByMember.set(member, (connectorCountByMember.get(member) ?? 0) + 1);
                const hasRegularConnectivity = this.hasRegularConnectivityAtPosition(hierarchy.combobulator, target.position, target.dimension);
                if (hasRegularConnectivity) {
                    const connectorTargets = regularConnectorTargetsByMember.get(member) ?? [];
                    connectorTargets.push(target);
                    regularConnectorTargetsByMember.set(member, connectorTargets);
                }
            }
        }
        const added = new Set();
        for (const [member] of memberNames) {
            const regularConnectorTargets = regularConnectorTargetsByMember.get(member) ?? [];
            const connectorCount = connectorCountByMember.get(member) ?? 0;
            if (connectorCount > 0 && regularConnectorTargets.length === connectorCount) {
                continue;
            }
            const memberTargets = targets.filter((target) => target.source !== 'connector-entry' && (targetMembers.get(target) ?? []).includes(member));
            const [firstTarget, ...remainingTargets] = memberTargets;
            if (!firstTarget) {
                continue;
            }
            for (const target of remainingTargets) {
                this.addSyntheticMemberDimensionBridge(hierarchy.combobulator, firstTarget.position, target.position, member, islandKey, added);
            }
        }
        for (const target of targets) {
            for (const member of targetMembers.get(target) ?? []) {
                const name = memberNames.get(member);
                if (!name) {
                    continue;
                }
                const aliasedFromRegularLabel = name !== `${baseName?.name}.${member}`;
                if (target.source === 'connector-entry') {
                    if ((regularConnectorTargetsByMember.get(member)?.length ?? 0) === 0) {
                        continue;
                    }
                    if (baseName?.source === 'hierarchy-label') {
                        continue;
                    }
                    this.addSyntheticNetLabel(hierarchy.combobulator, target.position, target.dimension, name, member, added, aliasedFromRegularLabel, baseName?.source === 'carrier-net-label');
                    continue;
                }
                const regularConnectorTargets = regularConnectorTargetsByMember.get(member) ?? [];
                const connectorHasRegularConnectivity = regularConnectorTargets.length > 0;
                if (connectorHasRegularConnectivity) {
                    for (const connectorTarget of regularConnectorTargets) {
                        this.addSyntheticMemberBridge(hierarchy.combobulator, target.position, connectorTarget.position, connectorTarget.dimension, member, islandKey, added);
                    }
                }
                const usesAliasedChildIdentity = childMemberNames.get(member) === name && name !== `${baseName?.name}.${member}`;
                if (baseName?.source !== 'hierarchy-label' || usesAliasedChildIdentity) {
                    this.addSyntheticMemberNetLabel(hierarchy.combobulator, target.position, name, member, islandKey, added, usesAliasedChildIdentity, aliasedFromRegularLabel, baseName?.source === 'carrier-net-label');
                }
                if (target.source === 'port' && target.label?.strategyMetadata?.['port-metadata']) {
                    this.addSyntheticPortLabel(hierarchy.combobulator, target.position, name, member, islandKey, target.label, added);
                    continue;
                }
                if (target.source === 'sheet-entry' && target.label?.strategyMetadata?.['sheet-entry-metadata']) {
                    this.addSyntheticSheetEntryLabel(hierarchy.combobulator, target.position, name, member, islandKey, target.label, added);
                }
            }
        }
    }
    collectAliasedChildMemberNames(hierarchy, targets) {
        const sheetEntryTargets = targets.filter((target) => target.source === 'sheet-entry' && !!target.label);
        if (sheetEntryTargets.length < 2) {
            return new Map();
        }
        const namesByMember = new Map();
        for (const target of sheetEntryTargets) {
            const sheetEntryMetadata = target.label.strategyMetadata?.['sheet-entry-metadata'];
            if (!sheetEntryMetadata) {
                continue;
            }
            for (const child of hierarchy.children) {
                if (child.instance.sheetSymbol?.uniqueId !== sheetEntryMetadata.sheetSymbolUniqueId) {
                    continue;
                }
                if (child.instance.isMultiChannel || child.instance.totalSheetInstances > 1) {
                    continue;
                }
                const childPort = this.findChildPortCarrierObject(child, sheetEntryMetadata.sheetEntryName);
                if (!childPort?.label) {
                    continue;
                }
                const childIsland = this.getCarrierIslandForObject(child.combobulator, childPort);
                const childTargets = this.collectTargets(child, childIsland);
                const positionKey = this.createDimensionPositionKeyer(child.combobulator, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
                const membersByTarget = this.resolveTargetMembers(childIsland, childTargets, positionKey);
                for (const childTarget of childTargets) {
                    if (childTarget.source !== 'connector-entry') {
                        continue;
                    }
                    const explicitName = this.findRegularNetLabelAtPosition(child.combobulator, childTarget.position, childTarget.dimension);
                    if (!explicitName) {
                        continue;
                    }
                    for (const member of membersByTarget.get(childTarget) ?? []) {
                        if (!this.isCompatibleChildMemberName(explicitName, member)) {
                            continue;
                        }
                        if (this.dereferenceLabelName(explicitName) === member) {
                            continue;
                        }
                        if (!namesByMember.has(member)) {
                            namesByMember.set(member, new Set());
                        }
                        namesByMember.get(member).add(explicitName);
                    }
                }
            }
        }
        const result = new Map();
        for (const [member, names] of namesByMember) {
            if (names.size === 1) {
                result.set(member, Array.from(names)[0]);
            }
        }
        return result;
    }
    isCompatibleChildMemberName(name, member) {
        const dereferencedName = this.dereferenceLabelName(name);
        if (dereferencedName === member || dereferencedName.endsWith(`.${member}`)) {
            return true;
        }
        if (!member.endsWith('n')) {
            return false;
        }
        const escapedPrefix = member.slice(0, -1).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`(?:^|\\.)${escapedPrefix}\\d+n$`).test(dereferencedName);
    }
    resolveTargetMembers(island, targets, positionKey, additionalCompatibleMembers = []) {
        const membersByTarget = new Map();
        const setMembers = (target, members) => {
            if (!membersByTarget.has(target)) {
                membersByTarget.set(target, new Set());
            }
            for (const member of members) {
                if (member.trim()) {
                    membersByTarget.get(target).add(member);
                }
            }
        };
        for (const target of targets) {
            if (target.source === 'connector-entry' && target.harnessMemberType) {
                continue;
            }
            setMembers(target, target.members);
        }
        const connectorTargetsByPosition = new Map();
        const targetsByPosition = new Map();
        const nestedAssignedTargets = new Set();
        for (const target of targets) {
            const key = positionKey(target.position);
            if (!targetsByPosition.has(key)) {
                targetsByPosition.set(key, []);
            }
            targetsByPosition.get(key).push(target);
            if (target.source !== 'connector-entry') {
                continue;
            }
            if (!connectorTargetsByPosition.has(key)) {
                connectorTargetsByPosition.set(key, []);
            }
            connectorTargetsByPosition.get(key).push(target);
        }
        for (const branchTarget of targets) {
            if (branchTarget.source !== 'connector-entry' || !branchTarget.harnessMemberType) {
                continue;
            }
            this.assignNestedBranchMembers(island, targetsByPosition, branchTarget, branchTarget.members[0], membersByTarget, nestedAssignedTargets, positionKey);
        }
        this.assignCompatibleNestedMembers(membersByTarget, additionalCompatibleMembers);
        return new Map(Array.from(membersByTarget.entries()).map(([target, members]) => [
            target,
            Array.from(members).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b)),
        ]));
    }
    assignCompatibleNestedMembers(membersByTarget, additionalCompatibleMembers = []) {
        const allMembers = Array.from(new Set([
            ...Array.from(membersByTarget.values()).flatMap((members) => Array.from(members)),
            ...additionalCompatibleMembers,
        ]));
        for (const [target, members] of membersByTarget) {
            if (target.source === 'connector-entry') {
                continue;
            }
            for (const member of Array.from(members)) {
                for (const candidate of allMembers) {
                    if (candidate !== member && candidate.startsWith(`${member}.`)) {
                        members.add(candidate);
                    }
                }
            }
        }
    }
    assignNestedBranchMembers(island, targetsByPosition, branchTarget, prefix, membersByTarget, nestedAssignedTargets, positionKey) {
        const reachablePositions = this.collectBranchReachablePositions(island, branchTarget.position, positionKey);
        for (const reachablePositionKey of reachablePositions) {
            for (const target of targetsByPosition.get(reachablePositionKey) ?? []) {
                if (target === branchTarget) {
                    continue;
                }
                if (target.harnessMemberType) {
                    this.assignNestedBranchMembers(island, targetsByPosition, target, `${prefix}.${target.members[0]}`, membersByTarget, nestedAssignedTargets, positionKey);
                    continue;
                }
                if (!nestedAssignedTargets.has(target)) {
                    membersByTarget.set(target, new Set());
                    nestedAssignedTargets.add(target);
                }
                for (const member of target.members) {
                    membersByTarget.get(target).add(`${prefix}.${member}`);
                }
            }
        }
    }
    collectBranchReachablePositions(island, startPosition, positionKey) {
        const edges = this.collectCarrierEdges(island);
        const edgesByPosition = new Map();
        for (const edge of edges) {
            for (const position of [edge.from, edge.to]) {
                const key = positionKey(position);
                if (!edgesByPosition.has(key)) {
                    edgesByPosition.set(key, []);
                }
                edgesByPosition.get(key).push(edge);
            }
        }
        const startKey = positionKey(startPosition);
        const visitedPositions = new Set();
        const visitedEdges = new Set();
        const queue = [];
        for (const edge of edgesByPosition.get(startKey) ?? []) {
            if (edge.internal) {
                continue;
            }
            const other = this.edgeOtherPosition(edge, startPosition, positionKey);
            if (other) {
                visitedEdges.add(this.edgeKey(edge, positionKey));
                queue.push(other);
            }
        }
        while (queue.length > 0) {
            const position = queue.shift();
            const currentPositionKey = positionKey(position);
            if (visitedPositions.has(currentPositionKey)) {
                continue;
            }
            visitedPositions.add(currentPositionKey);
            for (const edge of edgesByPosition.get(currentPositionKey) ?? []) {
                const edgeKey = this.edgeKey(edge, positionKey);
                if (visitedEdges.has(edgeKey)) {
                    continue;
                }
                visitedEdges.add(edgeKey);
                const other = this.edgeOtherPosition(edge, position, positionKey);
                if (other && positionKey(other) !== startKey) {
                    queue.push(other);
                }
            }
        }
        return visitedPositions;
    }
    collectCarrierEdges(island) {
        const edges = [];
        for (const object of island) {
            if (!object.wire) {
                continue;
            }
            const endpoints = object.endpoints.filter((endpoint) => endpoint.dimension === String(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness));
            if (endpoints.length < 2) {
                continue;
            }
            edges.push({
                from: endpoints[0].position,
                to: endpoints[1].position,
                internal: object.wire.strategyMetadata?.harness?.harnessSource === 'connector-internal',
            });
        }
        return edges;
    }
    collectTargets(hierarchy, island) {
        const targets = [];
        for (const object of island) {
            const metadata = object.label?.strategyMetadata?.harness ?? object.wire?.strategyMetadata?.harness;
            if (!metadata?.harnessSource) {
                continue;
            }
            const position = this.getObjectPositionInDimension(object, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
            if (!position) {
                continue;
            }
            if (metadata.harnessSource === 'connector-entry' && metadata.harnessMemberName) {
                if (!this.isConcreteHarnessType(metadata.harnessType)) {
                    continue;
                }
                if (!metadata.isHarnessDefinitionEntry) {
                    continue;
                }
                targets.push({
                    source: 'connector-entry',
                    position,
                    dimension: metadata.harnessMemberDimension,
                    members: [metadata.harnessMemberName],
                    harnessMemberType: metadata.harnessMemberType,
                    label: object.label,
                });
                continue;
            }
            if (metadata.harnessSource === 'port' && object.label) {
                let members = this.getExpandedMembersForHarnessType(hierarchy, metadata.harnessType);
                if (members.length === 0) {
                    members = this.inferMembersFromCarrierIsland(hierarchy, island, object.label);
                }
                if (members.length > 0) {
                    targets.push({ source: 'port', position, members, label: object.label });
                }
                continue;
            }
            if (metadata.harnessSource === 'sheet-entry' && object.label) {
                let members = this.getMembersForSheetEntry(hierarchy, object.label);
                if (members.length === 0) {
                    members = this.inferMembersFromCarrierIsland(hierarchy, island, object.label);
                }
                if (members.length > 0) {
                    targets.push({ source: 'sheet-entry', position, members, label: object.label });
                }
            }
        }
        return targets;
    }
    getMembersForHarnessType(hierarchy, harnessType) {
        if (!this.isConcreteHarnessType(harnessType)) {
            return [];
        }
        return hierarchy.instance.hierarchyEntry.schematic.getHarnessDefinitionMembers(harnessType);
    }
    getHarnessDefinitionEntries(hierarchy, harnessType) {
        if (!this.isConcreteHarnessType(harnessType)) {
            return [];
        }
        const schematic = hierarchy.instance.hierarchyEntry.schematic;
        const entries = schematic.getHarnessDefinitionEntries?.(harnessType);
        if (!entries?.length) {
            return (schematic.getHarnessDefinitionMembers?.(harnessType) ?? []).map((name) => ({ name }));
        }
        const nestedTypesByMember = this.getConnectorEntryNestedTypes(hierarchy.combobulator, harnessType);
        return entries.map((entry) => this.resolveDefinitionEntryHarnessType(entry, nestedTypesByMember));
    }
    resolveDefinitionEntryHarnessType(entry, nestedTypesByMember) {
        if (nestedTypesByMember.has(entry.name)) {
            return { name: entry.name, harnessType: nestedTypesByMember.get(entry.name) };
        }
        const memberNames = AltiumBusRange_1.AltiumBusRange.parse(entry.name)?.members ?? [entry.name];
        const observedTypes = memberNames
            .filter((memberName) => nestedTypesByMember.has(memberName))
            .map((memberName) => nestedTypesByMember.get(memberName));
        if (observedTypes.length === 0) {
            return entry;
        }
        const resolvedType = observedTypes[0];
        const foldedResolvedType = resolvedType?.toLowerCase();
        const allMembersAgree = observedTypes.length === memberNames.length &&
            observedTypes.every((observedType) => observedType?.toLowerCase() === foldedResolvedType);
        return allMembersAgree ? { name: entry.name, harnessType: resolvedType } : { name: entry.name };
    }
    getConnectorEntryNestedTypes(combobulator, harnessType) {
        const nestedTypesByMember = new Map();
        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
            return nestedTypesByMember;
        }
        const foldedHarnessType = harnessType.toLowerCase();
        for (const object of combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness).iterateAllObjects()) {
            const metadata = object.label?.strategyMetadata?.harness;
            if (metadata?.harnessSource !== 'connector-entry' ||
                !metadata.isHarnessDefinitionEntry ||
                metadata.harnessType?.toLowerCase() !== foldedHarnessType ||
                !metadata.harnessMemberName) {
                continue;
            }
            const nestedType = nestedTypesByMember.get(metadata.harnessMemberName) ?? metadata.harnessMemberType;
            nestedTypesByMember.set(metadata.harnessMemberName, nestedType);
        }
        return nestedTypesByMember;
    }
    getExpandedMembersForHarnessType(hierarchy, harnessType, visited = new Set()) {
        if (!this.isConcreteHarnessType(harnessType) || visited.has(harnessType.toLowerCase())) {
            return [];
        }
        visited.add(harnessType.toLowerCase());
        const members = [];
        for (const entry of this.getHarnessDefinitionEntries(hierarchy, harnessType)) {
            if (entry.harnessType) {
                const childMembers = this.getExpandedMembersForHarnessType(hierarchy, entry.harnessType, new Set(visited));
                if (childMembers.length > 0) {
                    members.push(...childMembers.map((member) => `${entry.name}.${member}`));
                    continue;
                }
            }
            members.push(...(AltiumBusRange_1.AltiumBusRange.parse(entry.name)?.members ?? [entry.name]));
        }
        return members;
    }
    isConcreteHarnessType(harnessType) {
        return !!harnessType?.trim() && harnessType.trim() !== '*';
    }
    getMembersForSheetEntry(hierarchy, label) {
        const harnessType = label.strategyMetadata?.harness?.harnessType;
        const localMembers = this.getExpandedMembersForHarnessType(hierarchy, harnessType);
        const sheetEntryMetadata = label.strategyMetadata?.['sheet-entry-metadata'];
        if (!sheetEntryMetadata) {
            return localMembers;
        }
        const childMembers = [];
        for (const child of hierarchy.children) {
            if (child.instance.sheetSymbol?.uniqueId !== sheetEntryMetadata.sheetSymbolUniqueId) {
                continue;
            }
            const childHarnessType = this.findChildPortHarnessType(child, sheetEntryMetadata.sheetEntryName);
            const expandedChildMembers = this.getExpandedMembersForHarnessType(child, childHarnessType);
            const inferredChildMembers = this.inferMembersForChildPort(child, sheetEntryMetadata.sheetEntryName);
            if (expandedChildMembers.length > 0) {
                childMembers.push(...this.expandMembersWithCompatibleMembers(expandedChildMembers, inferredChildMembers));
                continue;
            }
            if (inferredChildMembers.length > 0) {
                childMembers.push(...inferredChildMembers);
            }
        }
        if (localMembers.length === 0) {
            return Array.from(new Set(childMembers)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
        }
        return Array.from(new Set([...localMembers, ...childMembers])).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
    }
    expandMembersWithCompatibleMembers(members, compatibleMembers) {
        if (compatibleMembers.length === 0) {
            return members;
        }
        const expandedMembers = new Set(members);
        for (const member of members) {
            for (const compatibleMember of compatibleMembers) {
                if (compatibleMember === member || compatibleMember.startsWith(`${member}.`)) {
                    expandedMembers.add(compatibleMember);
                }
            }
        }
        return Array.from(expandedMembers).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
    }
    inferMembersForChildPort(hierarchy, portName) {
        const portObject = this.findChildPortCarrierObject(hierarchy, portName);
        if (!portObject?.label) {
            return [];
        }
        const island = this.getCarrierIslandForObject(hierarchy.combobulator, portObject);
        if (island.length === 0) {
            return [];
        }
        const targets = this.collectTargets(hierarchy, island);
        const portTargets = targets.filter((target) => target.source === 'port' && target.label === portObject.label);
        if (portTargets.length > 0) {
            const positionKey = this.createDimensionPositionKeyer(hierarchy.combobulator, AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
            const targetMembers = this.resolveTargetMembers(island, targets, positionKey);
            const resolvedMembers = portTargets.flatMap((target) => targetMembers.get(target) ?? []);
            if (resolvedMembers.length > 0) {
                return Array.from(new Set(resolvedMembers)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
            }
        }
        return this.inferMembersFromCarrierIsland(hierarchy, island, portObject.label);
    }
    getCarrierIslandForObject(combobulator, object) {
        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
            return [];
        }
        const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
        const island = new Set();
        for (const endpoint of object.endpoints) {
            if (endpoint.dimension !== String(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
                continue;
            }
            for (const connectedObject of harnessDimension.getObjectsConnectedToPosition(endpoint.position)) {
                if (this.isCarrierObject(connectedObject)) {
                    island.add(connectedObject);
                }
            }
        }
        return Array.from(island);
    }
    inferMembersFromCarrierIsland(hierarchy, island, excludeLabel) {
        const members = new Set();
        const addMembers = (candidateMembers) => {
            for (const member of candidateMembers) {
                if (member.trim()) {
                    members.add(member);
                }
            }
        };
        for (const object of island) {
            const label = object.label;
            const metadata = label?.strategyMetadata?.harness;
            if (!label || label === excludeLabel || !metadata?.harnessSource) {
                continue;
            }
            if (metadata.harnessSource === 'connector-entry' && metadata.harnessMemberName && !metadata.harnessMemberType) {
                if (this.isConcreteHarnessType(metadata.harnessType) && metadata.isHarnessDefinitionEntry) {
                    members.add(metadata.harnessMemberName);
                }
                continue;
            }
            if (metadata.harnessSource === 'port') {
                addMembers(this.getExpandedMembersForHarnessType(hierarchy, metadata.harnessType));
                continue;
            }
            if (metadata.harnessSource === 'sheet-entry') {
                addMembers(this.getMembersForSheetEntry(hierarchy, label));
            }
        }
        return Array.from(members).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
    }
    findChildPortHarnessType(hierarchy, portName) {
        const object = this.findChildPortCarrierObject(hierarchy, portName);
        return object?.label?.strategyMetadata?.harness?.harnessType;
    }
    findChildPortCarrierObject(hierarchy, portName) {
        if (!hierarchy.combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
            return undefined;
        }
        const harnessDimension = hierarchy.combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
        for (const object of harnessDimension.iterateLabelObjects('port')) {
            if (object.label?.strategyMetadata?.['port-metadata']?.portName !== portName) {
                continue;
            }
            return object;
        }
        return undefined;
    }
    collectCarrierBaseName(combobulator, island) {
        const carrierNetLabelNames = this.collectCarrierBaseNamesForSources(island, ['carrier-net-label']);
        if (carrierNetLabelNames.length > 0) {
            return { name: carrierNetLabelNames[0], source: 'carrier-net-label' };
        }
        const regularNetLabelNames = this.collectRegularNetLabelBaseNames(combobulator, island);
        if (regularNetLabelNames.length > 0) {
            return { name: regularNetLabelNames[0], source: 'regular-net-label' };
        }
        const portAndSheetEntryNames = this.collectCarrierBaseNamesForSources(island, ['port', 'sheet-entry']);
        if (portAndSheetEntryNames.length > 0) {
            return {
                name: this.qualifyHierarchyLabelBaseName(combobulator, island, portAndSheetEntryNames[0]),
                source: 'hierarchy-label',
            };
        }
        return undefined;
    }
    qualifyHierarchyLabelBaseName(combobulator, island, name) {
        const foldedName = name.toLowerCase();
        const ownHarnessType = this.selectOwnSheetEntryHarnessType(island, foldedName);
        if (!ownHarnessType || !combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
            return name;
        }
        const islandObjects = new Set(island);
        const foldedOwnHarnessType = ownHarnessType.toLowerCase();
        const collidesWithDifferentType = this.getSheetEntryCarrierIndex(combobulator).some((entry) => !islandObjects.has(entry.object) &&
            entry.foldedName === foldedName &&
            entry.foldedHarnessType !== foldedOwnHarnessType);
        return collidesWithDifferentType ? `${ownHarnessType}#${name}` : name;
    }
    selectOwnSheetEntryHarnessType(island, foldedName) {
        let selected;
        for (const object of island) {
            const label = object.label;
            const metadata = label?.strategyMetadata?.harness;
            if (metadata?.harnessSource !== 'sheet-entry' ||
                this.dereferenceLabelName(label?.originalLabel ?? '').toLowerCase() !== foldedName) {
                continue;
            }
            const harnessType = metadata.harnessType;
            if (!this.isConcreteHarnessType(harnessType)) {
                continue;
            }
            if (selected === undefined || sigma_engine_shared_1.Utils.naturalStringSort(harnessType.toLowerCase(), selected.toLowerCase()) < 0) {
                selected = harnessType;
            }
        }
        return selected;
    }
    getSheetEntryCarrierIndex(combobulator) {
        const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
        const cached = this.sheetEntryCarrierIndexes.get(harnessDimension);
        if (cached) {
            return cached;
        }
        const index = [];
        for (const object of harnessDimension.iterateAllObjects()) {
            const metadata = object.label?.strategyMetadata?.harness;
            if (metadata?.harnessSource !== 'sheet-entry' || !this.isConcreteHarnessType(metadata.harnessType)) {
                continue;
            }
            index.push({
                object,
                foldedName: this.dereferenceLabelName(object.label?.originalLabel ?? '').toLowerCase(),
                foldedHarnessType: (metadata.harnessType ?? '').toLowerCase(),
            });
        }
        this.sheetEntryCarrierIndexes.set(harnessDimension, index);
        return index;
    }
    collectCarrierBaseNamesForSources(island, sources) {
        const sourceSet = new Set(sources);
        const names = island
            .filter((object) => {
            const source = object.label?.strategyMetadata?.harness?.harnessSource;
            return source ? sourceSet.has(source) : false;
        })
            .map((object) => object.label)
            .filter((label) => !!label)
            .map((label) => label.originalLabel)
            .filter((name) => !!name?.trim())
            .map((name) => this.dereferenceLabelName(name));
        return Array.from(new Set(names)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
    }
    collectTypedHierarchyIdentities(hierarchy, island) {
        const identities = [];
        for (const object of island) {
            const label = object.label;
            const metadata = label?.strategyMetadata?.harness;
            if (!label || (metadata?.harnessSource !== 'port' && metadata?.harnessSource !== 'sheet-entry')) {
                continue;
            }
            const harnessType = metadata.harnessType;
            if (!this.isConcreteHarnessType(harnessType)) {
                continue;
            }
            const members = this.getExpandedMembersForHarnessType(hierarchy, harnessType);
            if (members.length === 0) {
                continue;
            }
            const name = this.dereferenceLabelName(label.originalLabel).toLowerCase();
            if (!name.trim()) {
                continue;
            }
            identities.push({
                kind: metadata.harnessSource === 'port' ?
                    HarnessHierarchyIdentityKind.Port
                    : HarnessHierarchyIdentityKind.SheetEntry,
                name,
                harnessType: harnessType.toLowerCase(),
                members: Array.from(new Set(members)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b)),
            });
        }
        return identities;
    }
    islandHasLocalBreakout(island) {
        return island.some((object) => {
            const metadata = object.label?.strategyMetadata?.harness ?? object.wire?.strategyMetadata?.harness;
            return (metadata?.harnessSource === 'connector-entry' &&
                !!metadata.harnessMemberName &&
                !!metadata.isHarnessDefinitionEntry &&
                this.isConcreteHarnessType(metadata.harnessType));
        });
    }
    areHierarchyIdentitiesCompatible(a, b) {
        if (a.name !== b.name) {
            return false;
        }
        const sameType = a.harnessType === b.harnessType;
        const sameMembers = a.members.length === b.members.length && a.members.every((member, index) => member === b.members[index]);
        return sameType || sameMembers;
    }
    identitiesShareCompatibleMatch(identitiesA, identitiesB, allowSingleMember) {
        return identitiesA.some((a) => identitiesB.some((b) => {
            if (a.kind === HarnessHierarchyIdentityKind.SheetEntry && b.kind === HarnessHierarchyIdentityKind.SheetEntry) {
                return false;
            }
            if (!allowSingleMember && (a.members.length < 2 || b.members.length < 2)) {
                return false;
            }
            return this.areHierarchyIdentitiesCompatible(a, b);
        }));
    }
    mergeViolatesIslandGuards(islandIdentities, islandIsPureLocalBreakout, groupA, groupB) {
        return groupA.some((a) => groupB.some((b) => this.identitiesRepeatASheetEntry(islandIdentities[a], islandIdentities[b]) ||
            this.pureLocalBreakoutIsRelayAmbiguous(islandIdentities[a], islandIsPureLocalBreakout[a], islandIdentities[b], islandIsPureLocalBreakout[b])));
    }
    identitiesRepeatASheetEntry(identitiesA, identitiesB) {
        return sigma_engine_shared_1.Utils.ChainableIterator.from(identitiesA)
            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.SheetEntry)
            .some((a) => sigma_engine_shared_1.Utils.ChainableIterator.from(identitiesB)
            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.SheetEntry)
            .some((b) => this.areHierarchyIdentitiesCompatible(a, b)));
    }
    pureLocalBreakoutIsRelayAmbiguous(identitiesA, isPureLocalA, identitiesB, isPureLocalB) {
        if (isPureLocalA === isPureLocalB) {
            return false;
        }
        const [local, other] = isPureLocalA ? [identitiesA, identitiesB] : [identitiesB, identitiesA];
        if (!sigma_engine_shared_1.Utils.ChainableIterator.from(other)
            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.SheetEntry)
            .some(() => true)) {
            return false;
        }
        return sigma_engine_shared_1.Utils.ChainableIterator.from(local)
            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.Port)
            .some((a) => sigma_engine_shared_1.Utils.ChainableIterator.from(other)
            .pipeFilter((identity) => identity.kind === HarnessHierarchyIdentityKind.Port)
            .some((b) => a.name === b.name && a.harnessType === b.harnessType));
    }
    collectRegularNetLabelBaseNames(combobulator, island) {
        return this.collectRegularLabelBaseNames(combobulator, island, ['net-label']);
    }
    collectRegularLabelBaseNames(combobulator, island, labelTypes) {
        if (!combobulator.hasDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness)) {
            return [];
        }
        const harnessDimension = combobulator.getDimension(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness);
        const islandObjects = new Set(island);
        const names = [];
        const collectLabel = (label) => {
            if (!label || !labelTypes.includes(label.labelType)) {
                return;
            }
            if (harnessDimension
                .getObjectsAtPosition(label.position)
                .some((object) => islandObjects.has(object) && object.label?.strategyMetadata?.harness?.harnessSource === 'connector-entry')) {
                return;
            }
            if (label.strategyMetadata?.harness?.isCarrier ||
                label.strategyMetadata?.harness?.harnessSource?.startsWith('synthetic-')) {
                return;
            }
            const name = this.dereferenceLabelName(label.originalLabel);
            if (name.trim()) {
                names.push(name);
            }
        };
        for (const labelType of labelTypes) {
            for (const labelObject of combobulator.defaultDimension.iterateLabelObjects(labelType)) {
                const label = labelObject.label;
                if (!label) {
                    continue;
                }
                const touchesIsland = harnessDimension
                    .getObjectsAtPosition(label.position)
                    .some((object) => islandObjects.has(object) && this.isCarrierBaseGeometryObject(object));
                if (touchesIsland) {
                    collectLabel(label);
                }
            }
        }
        for (const position of this.collectCarrierBaseTraversalPositions(island)) {
            for (const object of combobulator.defaultDimension.getObjectsConnectedToPosition(position)) {
                collectLabel(object.label);
            }
        }
        return Array.from(new Set(names)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b));
    }
    collectCarrierBaseTraversalPositions(island) {
        const positions = [];
        const seen = new Set();
        const addPosition = (position) => {
            const key = this.positionKey(position);
            if (seen.has(key)) {
                return;
            }
            seen.add(key);
            positions.push(position);
        };
        for (const object of island) {
            if (object.label && this.isCarrierBaseLabelObject(object)) {
                addPosition(object.label.position);
            }
        }
        return positions;
    }
    isCarrierBaseGeometryObject(object) {
        if (this.isCarrierBaseLabelObject(object)) {
            return true;
        }
        return object.wire?.strategyMetadata?.harness?.harnessSource === 'signal-harness';
    }
    isCarrierBaseLabelObject(object) {
        const source = object.label?.strategyMetadata?.harness?.harnessSource;
        return source === 'carrier-net-label' || source === 'port' || source === 'sheet-entry';
    }
    findRegularNetLabelAtPosition(combobulator, position, dimension) {
        const regularDimension = this.getRegularDimension(combobulator, dimension);
        const connectedLabels = Array.from(regularDimension.getObjectsConnectedToPosition(position))
            .map((object) => object.label)
            .filter((label) => label?.labelType === 'net-label')
            .filter((label) => !label.strategyMetadata?.harness?.isCarrier);
        const syntheticLabels = connectedLabels
            .filter((label) => label.strategyMetadata?.harness?.harnessSource === 'synthetic-net-label')
            .map((label) => this.dereferenceLabelName(label.originalLabel))
            .filter((name) => !!name.trim());
        if (syntheticLabels.length > 0) {
            return Array.from(new Set(syntheticLabels)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b))[0];
        }
        const labels = connectedLabels
            .map((label) => this.dereferenceLabelName(label.originalLabel))
            .filter((name) => !!name.trim());
        if (labels.length === 0) {
            return undefined;
        }
        const sharedSyntheticLabels = this.findSyntheticLabelsSharingRegularLabel(combobulator, labels);
        if (sharedSyntheticLabels.length > 0) {
            return Array.from(new Set(sharedSyntheticLabels)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b))[0];
        }
        return Array.from(new Set(labels)).sort((a, b) => sigma_engine_shared_1.Utils.naturalStringSort(a, b))[0];
    }
    findSyntheticLabelsSharingRegularLabel(combobulator, regularNames) {
        const regularNameSet = new Set(regularNames.map((name) => this.dereferenceLabelName(name)));
        const syntheticNames = [];
        for (const labelObject of combobulator.defaultDimension.iterateLabelObjects('net-label')) {
            const label = labelObject.label;
            if (!label || label.strategyMetadata?.harness?.isCarrier) {
                continue;
            }
            const labelName = this.dereferenceLabelName(label.originalLabel);
            if (!regularNameSet.has(labelName)) {
                continue;
            }
            for (const connectedObject of combobulator.defaultDimension.getObjectsConnectedToPosition(label.position)) {
                const connectedLabel = connectedObject.label;
                if (connectedLabel?.labelType !== 'net-label' ||
                    connectedLabel.strategyMetadata?.harness?.harnessSource !== 'synthetic-net-label') {
                    continue;
                }
                const syntheticName = this.dereferenceLabelName(connectedLabel.originalLabel);
                if (syntheticName.trim()) {
                    syntheticNames.push(syntheticName);
                }
            }
        }
        return syntheticNames;
    }
    hasRegularConnectivityAtPosition(combobulator, position, dimension) {
        return Array.from(this.getRegularDimension(combobulator, dimension).getObjectsConnectedToPosition(position)).some((object) => !object.label?.strategyMetadata?.harness?.isCarrier);
    }
    getRegularDimension(combobulator, dimension) {
        return dimension && combobulator.hasDimension(dimension) ?
            combobulator.getDimension(dimension)
            : combobulator.defaultDimension;
    }
    addSyntheticNetLabel(combobulator, position, dimension, name, member, added, aliasedFromRegularLabel = false, bundleNamedByCarrierLabel = false) {
        const key = `net-label:${position[0]}:${position[1]}:${member}:${name}`;
        if (added.has(key)) {
            return;
        }
        added.add(key);
        combobulator.setLabel(this.wireEndpoint(position, dimension), name, 'net-label', {
            harness: {
                harnessMemberName: member,
                preservePhysicalName: false,
                aliasedFromRegularLabel,
                bundleNamedByCarrierLabel,
                harnessSource: 'synthetic-net-label',
            },
        });
    }
    addSyntheticMemberNetLabel(combobulator, position, name, member, islandKey, added, preservePhysicalName = false, aliasedFromRegularLabel = false, bundleNamedByCarrierLabel = false) {
        const key = `member-net-label:${position[0]}:${position[1]}:${member}:${name}`;
        if (added.has(key)) {
            return;
        }
        added.add(key);
        combobulator.setLabel([this.memberEndpoint(position, member, islandKey)], name, 'net-label', {
            harness: {
                harnessMemberName: member,
                preservePhysicalName,
                aliasedFromRegularLabel,
                bundleNamedByCarrierLabel,
                harnessSource: 'synthetic-net-label',
            },
        });
    }
    addSyntheticMemberBridge(combobulator, fromPosition, toPosition, toDimension, member, islandKey, added) {
        const key = `bridge:${fromPosition[0]}:${fromPosition[1]}:${toPosition[0]}:${toPosition[1]}:${member}`;
        if (added.has(key)) {
            return;
        }
        added.add(key);
        combobulator.addWire({
            from: this.memberEndpoint(fromPosition, member, islandKey),
            to: this.wireEndpoint(toPosition, toDimension)[0],
            strategyMetadata: {
                harness: {
                    isCarrier: false,
                    harnessMemberName: member,
                    harnessSource: 'synthetic-member-bridge',
                },
            },
        });
    }
    addSyntheticMemberDimensionBridge(combobulator, fromPosition, toPosition, member, islandKey, added) {
        const key = `member-dimension-bridge:${fromPosition[0]}:${fromPosition[1]}:${toPosition[0]}:${toPosition[1]}:${member}`;
        if (added.has(key)) {
            return;
        }
        added.add(key);
        combobulator.addWire({
            from: this.memberEndpoint(fromPosition, member, islandKey),
            to: this.memberEndpoint(toPosition, member, islandKey),
            strategyMetadata: {
                harness: {
                    isCarrier: false,
                    harnessMemberName: member,
                    harnessSource: 'synthetic-member-bridge',
                },
            },
        });
    }
    addSyntheticPortLabel(combobulator, position, name, member, islandKey, sourceLabel, added) {
        const portMetadata = sourceLabel.strategyMetadata?.['port-metadata'];
        if (!portMetadata) {
            return;
        }
        const key = `port:${position[0]}:${position[1]}:${member}:${name}`;
        if (added.has(key)) {
            return;
        }
        added.add(key);
        combobulator.setLabel([this.memberEndpoint(position, member, islandKey)], name, 'port', {
            'port-metadata': portMetadata,
            harness: {
                ...sourceLabel.strategyMetadata?.harness,
                isCarrier: false,
                harnessMemberName: member,
                harnessSource: 'synthetic-port',
            },
        });
    }
    addSyntheticSheetEntryLabel(combobulator, position, name, member, islandKey, sourceLabel, added) {
        const sheetEntryMetadata = sourceLabel.strategyMetadata?.['sheet-entry-metadata'];
        if (!sheetEntryMetadata) {
            return;
        }
        const key = `sheet-entry:${position[0]}:${position[1]}:${member}:${name}`;
        if (added.has(key)) {
            return;
        }
        added.add(key);
        combobulator.setLabel([this.memberEndpoint(position, member, islandKey)], name, 'sheet-entry', {
            'sheet-entry-metadata': sheetEntryMetadata,
            harness: {
                ...sourceLabel.strategyMetadata?.harness,
                isCarrier: false,
                harnessMemberName: member,
                harnessSource: 'synthetic-sheet-entry',
            },
        }, { canNameNet: false });
    }
    wireEndpoint(position, dimension) {
        return [{ position, dimension: dimension ?? AltiumLabelTypes_1.AltiumCombobulationDimensions.Wire }];
    }
    memberEndpoint(position, member, islandKey) {
        return { position, dimension: `${AltiumLabelTypes_1.AltiumCombobulationDimensions.Wire}:harness-member:${islandKey}:${member}` };
    }
    removeCarrierObjects(combobulator) {
        const carrierObjects = Array.from(combobulator.iterateAllObjects()).filter((object) => this.isCarrierObject(object));
        for (const object of carrierObjects) {
            combobulator.removeObject(object);
        }
    }
    isCarrierObject(object) {
        if (object.label?.strategyMetadata?.harness?.isCarrier) {
            return true;
        }
        if (object.wire?.strategyMetadata?.harness?.isCarrier) {
            return true;
        }
        return object.endpoints.some((endpoint) => endpoint.dimension === String(AltiumLabelTypes_1.AltiumCombobulationDimensions.SignalHarness));
    }
    getObjectPositionInDimension(object, dimension) {
        const endpoint = object.endpoints.find((candidate) => candidate.dimension === dimension);
        if (endpoint) {
            return endpoint.position;
        }
        if (object.label) {
            return object.label.position;
        }
        return undefined;
    }
    positionKey(position) {
        return `${position[0]}:${position[1]}`;
    }
    createDimensionPositionKeyer(combobulator, dimensionName) {
        const dimension = combobulator.getDimension(dimensionName);
        return (position) => `${dimension.roundPoint(position[0])}:${dimension.roundPoint(position[1])}`;
    }
    edgeKey(edge, positionKey) {
        const fromKey = positionKey(edge.from);
        const toKey = positionKey(edge.to);
        return fromKey < toKey ? `${fromKey}|${toKey}|${edge.internal}` : `${toKey}|${fromKey}|${edge.internal}`;
    }
    edgeOtherPosition(edge, position, positionKey) {
        const key = positionKey(position);
        if (positionKey(edge.from) === key) {
            return edge.to;
        }
        if (positionKey(edge.to) === key) {
            return edge.from;
        }
        return undefined;
    }
    dereferenceLabelName(name) {
        try {
            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(name);
        }
        catch {
            return name;
        }
    }
}
exports.AltiumHarnessLocalNamingRule = AltiumHarnessLocalNamingRule;
//# sourceMappingURL=AltiumHarnessLocalNamingRule.js.map